import React from "react";
import {
  Inbox,
  PenLine,
  ShieldCheck,
  Ban,
  XCircle,
} from "lucide-react";

/**
 * Timeline d'audit pour une demande de restitution de matériel.
 *
 * Construit la chronologie des événements à partir des champs présents dans
 * `request` (equipment_return_requests) :
 *  - created_at + requested_by_name           → Demande émise
 *  - collab_signed_at (status awaiting/confirmed) → Collaborateur a signé
 *  - last_rejection_at + last_rejection_*     → Refus du contrôleur (rejection_count fois)
 *  - controller_signed_at + controller_name   → Contre-signature contrôleur (validation définitive)
 *  - cancelled_at + cancelled_by_name         → Annulation
 *
 * Note : Quand une demande passe par plusieurs cycles (signature → refus →
 * re-signature → validation), seuls le DERNIER refus et la signature COURANTE
 * sont conservés dans l'objet. Le `rejection_count` permet d'afficher "N
 * refus précédent(s)" en agrégat.
 */
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STEP_CFG = {
  created:    { Icon: Inbox,        cls: "bg-zinc-50 border-zinc-200 text-zinc-700",          dot: "bg-zinc-500" },
  signed:     { Icon: PenLine,      cls: "bg-amber-50 border-amber-200 text-amber-800",       dot: "bg-amber-500" },
  rejected:   { Icon: XCircle,      cls: "bg-red-50 border-red-200 text-red-700",             dot: "bg-red-500" },
  confirmed:  { Icon: ShieldCheck,  cls: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500" },
  cancelled:  { Icon: Ban,          cls: "bg-zinc-100 border-zinc-300 text-zinc-700",         dot: "bg-zinc-400" },
};

export default function EquipmentReturnTimeline({ request, testidPrefix = "return-timeline" }) {
  if (!request) return null;

  const steps = [];

  // 1) Demande émise
  steps.push({
    key: "created",
    when: request.created_at,
    title: "Demande de restitution émise",
    by: request.requested_by_name || "—",
    detail: request.note ? `« ${request.note} »` : null,
  });

  // 2) Refus précédent (le plus récent), si applicable
  if (request.last_rejection_reason && request.last_rejection_at) {
    const count = Number(request.rejection_count || 1);
    steps.push({
      key: "rejected",
      when: request.last_rejection_at,
      title: count > 1
        ? `Refusée par le contrôleur (refus n°${count})`
        : "Refusée par le contrôleur",
      by: request.last_rejection_by_name || "—",
      detail: `Motif : « ${request.last_rejection_reason} »`,
    });
  }

  // 3) Signature collab (présente si awaiting_controller ou confirmed)
  if (request.collab_signed_at && (request.status === "awaiting_controller" || request.status === "confirmed")) {
    const etatLabel = {
      bon: "Bon état",
      use: "Usé",
      endommage: "Endommagé",
      perte: "Inutilisable", // legacy → libellé unifié
      inutilisable: "Inutilisable",
    }[request.etat] || request.etat || "—";
    steps.push({
      key: "signed",
      when: request.collab_signed_at,
      title: "Signée par le collaborateur",
      by: request.collab_name || "—",
      detail: `État déclaré : ${etatLabel}${request.etat_note ? ` — « ${request.etat_note} »` : ""}`,
    });
  }

  // 4) Contre-signature contrôleur (validation définitive)
  if (request.status === "confirmed" && request.controller_signed_at) {
    steps.push({
      key: "confirmed",
      when: request.controller_signed_at,
      title: "Contre-signée par le contrôleur — validation définitive",
      by: request.controller_name || "—",
      detail: "Le matériel est libéré du stock du collaborateur.",
    });
  }

  // 5) Annulation
  if (request.status === "cancelled" && request.cancelled_at) {
    steps.push({
      key: "cancelled",
      when: request.cancelled_at,
      title: "Demande annulée",
      by: request.cancelled_by_name || "—",
      detail: null,
    });
  }

  // Tri chronologique croissant
  steps.sort((a, b) => (a.when || "").localeCompare(b.when || ""));

  return (
    <div className="space-y-3" data-testid={testidPrefix}>
      <p className="label-uppercase text-zinc-600">Journal de la restitution</p>
      <ol className="relative border-l-2 border-zinc-200 ml-3 space-y-4">
        {steps.map((s, idx) => {
          const cfg = STEP_CFG[s.key] || STEP_CFG.created;
          const Icon = cfg.Icon;
          return (
            <li
              key={`${s.key}-${idx}`}
              className="ml-5 relative"
              data-testid={`${testidPrefix}-step-${s.key}`}
            >
              <span
                className={`absolute -left-[33px] top-1 inline-flex items-center justify-center h-6 w-6 rounded-full border-2 border-white ${cfg.dot}`}
              >
                <Icon size={11} className="text-white" />
              </span>
              <div className={`border rounded-sm px-3 py-2 ${cfg.cls}`}>
                <p className="font-display font-semibold text-sm">{s.title}</p>
                <p className="text-[11px] font-mono mt-0.5 opacity-80">
                  {formatDateTime(s.when)} · par {s.by}
                </p>
                {s.detail && (
                  <p className="text-xs mt-1 leading-snug">{s.detail}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
